import type {
  CharacterCursorOptions,
  ClockCursorOptions,
  CursorEffectResult,
  EmojiCursorOptions,
  FairyDustCursorOptions,
  GhostCursorOptions,
  RainbowCursorOptions,
  SpringyEmojiCursorOptions,
  TextFlagOptions,
  TrailingCursorOptions,
} from 'cursor-effects';

function finiteNumber(value: string | undefined, fallback: number, min: number, max: number): number {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
}

function safeColour(value: string | undefined, fallback: string): string {
  return value && /^(?:#[0-9a-f]{3,8}|[a-z]+)$/i.test(value) ? value : fallback;
}

function safeImage(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.href);
    return url.protocol === 'https:' || url.origin === window.location.origin
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

type CursorEffectDefinition =
  | { factory: 'rainbowCursor'; options: RainbowCursorOptions }
  | { factory: 'emojiCursor'; options: EmojiCursorOptions }
  | { factory: 'springyEmojiCursor'; options: SpringyEmojiCursorOptions }
  | { factory: 'ghostCursor'; options: GhostCursorOptions }
  | { factory: 'trailingCursor'; options: TrailingCursorOptions }
  | { factory: 'textFlag'; options: TextFlagOptions }
  | { factory: 'followingDotCursor'; options: { color?: string } }
  | { factory: 'bubbleCursor'; options: Record<string, never> }
  | { factory: 'snowflakeCursor'; options: Record<string, never> }
  | { factory: 'fairyDustCursor'; options: FairyDustCursorOptions }
  | { factory: 'clockCursor'; options: ClockCursorOptions }
  | { factory: 'characterCursor'; options: CharacterCursorOptions };

export function cursorEffectDefinition(
  effect: string,
  parameters: string[],
): CursorEffectDefinition | undefined {
  switch (effect) {
    case 'rainbow': {
      if (!parameters.length) return { factory: 'rainbowCursor', options: {} };
      const lastIsSize = parameters.length > 1 && Number.isFinite(Number(parameters.at(-1)));
      const colors = parameters
        .slice(1, lastIsSize ? -1 : undefined)
        .map(colour => safeColour(colour, ''))
        .filter(Boolean);
      return {
        factory: 'rainbowCursor',
        options: {
          length: finiteNumber(parameters[0], 20, 1, 60),
          ...(colors.length ? { colors } : {}),
          ...(lastIsSize ? { size: finiteNumber(parameters.at(-1), 3, 1, 12) } : {}),
        },
      };
    }
    case 'emoji': {
      const lastIsDelay = parameters.length > 1 && Number.isFinite(Number(parameters.at(-1)));
      const emoji = parameters.slice(0, lastIsDelay ? -1 : undefined).slice(0, 8);
      return {
        factory: 'emojiCursor',
        options: {
          ...(emoji.length ? { emoji } : {}),
          ...(lastIsDelay ? { delay: finiteNumber(parameters.at(-1), 16, 1, 200) } : {}),
        },
      };
    }
    case 'elastic':
      return {
        factory: 'springyEmojiCursor',
        options: parameters[0] ? { emoji: parameters[0] } : {},
      };
    case 'ghost': {
      const image = safeImage(parameters[0]);
      return {
        factory: 'ghostCursor',
        options: image ? { image } : {},
      };
    }
    case 'trailing': {
      const image = safeImage(parameters[0]);
      return {
        factory: 'trailingCursor',
        options: {
          ...(image ? { baseImageSrc: image } : {}),
          ...(parameters[1]
            ? { particles: finiteNumber(parameters[1], 15, 1, 30) }
            : {}),
          ...(parameters[2] ? { rate: finiteNumber(parameters[2], 0.8, 0.1, 1) } : {}),
        },
      };
    }
    case 'textflag':
      return {
        factory: 'textFlag',
        options: {
          ...(parameters[0] ? { text: parameters[0].slice(0, 40) } : {}),
          ...(parameters[1] ? { color: safeColour(parameters[1], '#ff6800') } : {}),
          ...(parameters[2] ? { textSize: finiteNumber(parameters[2], 12, 6, 40) } : {}),
          ...(parameters[3] ? { font: parameters[3].slice(0, 60) } : {}),
          ...(parameters[4] ? { gap: finiteNumber(parameters[4], 14, 4, 80) } : {}),
        },
      };
    case 'dot':
      return {
        factory: 'followingDotCursor',
        options: parameters[0] ? { color: safeColour(parameters[0], '#323232') } : {},
      };
    case 'bubbles':
      return { factory: 'bubbleCursor', options: {} };
    case 'snow':
      return { factory: 'snowflakeCursor', options: {} };
    case 'fairy': {
      const colors = parameters.slice(0, 8)
        .map(colour => safeColour(colour, ''))
        .filter(Boolean);
      return {
        factory: 'fairyDustCursor',
        options: colors.length ? { colors } : {},
      };
    }
    case 'clock':
      return {
        factory: 'clockCursor',
        options: {
          ...(parameters[0] ? { dateColor: safeColour(parameters[0], 'blue') } : {}),
          ...(parameters[1] ? { faceColor: safeColour(parameters[1], 'black') } : {}),
          ...(parameters[2] ? { secondsColor: safeColour(parameters[2], 'red') } : {}),
          ...(parameters[3] ? { minutesColor: safeColour(parameters[3], 'black') } : {}),
          ...(parameters[4] ? { hoursColor: safeColour(parameters[4], 'black') } : {}),
        },
      };
    case 'character': {
      const colors = parameters.slice(2, 8)
        .map(colour => safeColour(colour, ''))
        .filter(Boolean);
      return {
        factory: 'characterCursor',
        options: {
          ...(parameters[0]
            ? { characters: parameters[0].slice(0, 20).split('') }
            : {}),
          ...(parameters[1] ? { font: parameters[1].slice(0, 60) } : {}),
          ...(colors.length ? { colors } : {}),
        },
      };
    }
    default:
      return undefined;
  }
}

export async function startCursorEffect(
  effect: string,
  parameters: string[],
): Promise<CursorEffectResult | undefined> {
  if (effect === 'none' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return undefined;
  }
  const definition = cursorEffectDefinition(effect, parameters);
  if (!definition) return undefined;
  const effects = await import('cursor-effects');
  switch (definition.factory) {
    case 'rainbowCursor': return effects.rainbowCursor(definition.options);
    case 'emojiCursor': return effects.emojiCursor(definition.options);
    case 'springyEmojiCursor': return effects.springyEmojiCursor(definition.options);
    case 'ghostCursor': return effects.ghostCursor(definition.options);
    case 'trailingCursor': return effects.trailingCursor(definition.options);
    case 'textFlag': return effects.textFlag(definition.options);
    case 'followingDotCursor': return effects.followingDotCursor(definition.options);
    case 'bubbleCursor': return effects.bubbleCursor(definition.options);
    case 'snowflakeCursor': return effects.snowflakeCursor(definition.options);
    case 'fairyDustCursor': return effects.fairyDustCursor(definition.options);
    case 'clockCursor': return effects.clockCursor(definition.options);
    case 'characterCursor': return effects.characterCursor(definition.options);
  }
}
